import { useQuery } from '@tanstack/react-query'
import { usePieceAutocomplete, type Piece } from './usePieceAutocomplete'
import type { GetComposerWorksResponse } from '@brent/shared/types'

export interface PieceAutocompleteResult {
  pieces: Piece[]
  isLoading: boolean
  error: Error | null
  source: 'static' | 'lambda' | null
}

/**
 * Enhanced piece autocomplete hook with Lambda + cache integration
 *
 * Always queries Lambda API (which includes cached entries and freetext),
 * and merges those results with static file results for best of both worlds.
 * Pass a searchFn to enable the Lambda fallback; without it only static results are used.
 *
 * @param composerId - Wikidata QID of the composer (e.g., "Q254")
 * @param searchTerm - Search term from user input
 * @param searchFn - Optional portal API function for Lambda fallback
 * @param limit - Max results to return (default: 10)
 * @returns Merged pieces from static file and Lambda cache, with loading/error states
 */
export const usePieceAutocompleteWithFallback = (
  composerId: string | null,
  searchTerm: string,
  searchFn?: (composerId: string, limit: number) => Promise<GetComposerWorksResponse>,
  limit: number = 10,
): PieceAutocompleteResult => {
  // Get static dataset results (instant if file exists)
  const staticResults = usePieceAutocomplete(composerId, searchTerm, limit)

  // ALWAYS query Lambda API to get cached entries (includes freetext and Wikidata)
  const {
    data: lambdaData,
    isLoading: isLoadingLambda,
    error: lambdaError,
  } = useQuery({
    queryKey: ['composer-works-lambda', composerId],
    queryFn: () => searchFn!(composerId!, 200), // Get up to 200 works
    enabled: !!composerId && searchTerm.length >= 2 && !!searchFn, // Query when we have composer, search term, and API fn
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    retry: 1, // Only retry once for Lambda calls
  })

  // Normalize function to remove accents for matching
  const normalize = (str: string) =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

  // If search term is too short, return empty
  if (searchTerm.length < 2) {
    return {
      pieces: [],
      isLoading: false,
      error: null,
      source: null,
    }
  }

  // Convert Lambda API response to Piece format
  const lambdaPieces: Piece[] =
    lambdaData?.works.map((w) => ({
      id: w.wikidataId || '', // Use empty string for freetext entries (no Wikidata ID)
      title: w.title,
      catalogNumber: w.catalogNumber,
      year: w.year,
      genre: undefined, // Not available from Lambda API
      searchTerms: [], // Generate from title and catalog number
      source: w.source || 'wikidata', // Include source field for freetext detection
    })) || []

  // Client-side filtering by search term with accent-insensitive matching
  const filteredLambdaPieces = lambdaPieces.filter((piece) => {
    const searchNormalized = normalize(searchTerm)
    const titleMatch = normalize(piece.title).includes(searchNormalized)
    const catalogMatch = piece.catalogNumber
      ? normalize(piece.catalogNumber).includes(searchNormalized)
      : false
    return titleMatch || catalogMatch
  })

  // Merge static and Lambda results, removing duplicates by ID
  // Prefer static results for Wikidata entries (more complete data)
  // Always include freetext entries from Lambda
  const staticIds = new Set(staticResults.map((p) => p.id).filter((id) => id))
  const uniqueLambdaResults = filteredLambdaPieces.filter(
    (piece) => piece.source === 'freetext' || !staticIds.has(piece.id),
  )

  const mergedResults = [...staticResults, ...uniqueLambdaResults].slice(0, limit)

  // Determine source based on what we have
  let source: 'static' | 'lambda' | null = null
  if (mergedResults.length > 0) {
    source = staticResults.length > 0 ? 'static' : 'lambda'
  }

  return {
    pieces: mergedResults,
    isLoading: isLoadingLambda,
    error: lambdaError as Error | null,
    source,
  }
}
